import React, {CSSProperties, ReactNode, useContext, useEffect, useRef, useState} from 'react'
import {
    DataGrid,
    DataGridDataRowProps,
    DataGridHeaderRowProps,
    DropdownInput,
    Icon,
    IconButton,
    IconProps,
    Modal,
    SORT_ORDER,
    SortElementProps,
    Spinner,
    Text,
} from "atmosfair-ui";
import {AgencyProps} from "../../../api/model/agency";
import {SortBundle, TransformedBundleResponse} from "../../../api/model/bundle";
import {v4 as uuidv4} from "uuid";
import {useTranslation} from "react-i18next";
import {useLazyLoadBundleQuery, useLazyReadBundlesQuery} from "../../../redux/reducer/api";
import {convertAgenciesFromDropdownFormat, convertAgenciesToDropdownFormat} from "../../../utils/converter/dropdown";
import "./styles.css"
import clsx from "clsx";
import {theme} from "../../../styles/theme";
import {useAppDispatch, useAppSelector} from "../../../redux/reducer/store";
import {capitalizeFirstLetter, formatDateString, NO_DATA_STRING} from "../../../utils/string/common";
import {loadJourney} from "../../../redux/reducer/journey";
import {
    attachTravelComponentsOnLoadTravelComponents,
    loadTravelComponents,
    replaceTravelComponentsOnLoadTravelComponents
} from "../../../redux/reducer/travel-components";
import {i18n} from "i18next";
import {getDaysCountBetweenDates, SMALL_SPINNER_SIZE} from "../../../utils/common";
import {useNavigate} from "react-router-dom";
import {PATH_STRINGS} from "../../../routes/route-props";
import {ScrollDirection} from "../../../api/model/common";
import {BundleType} from "../common";
import {toast} from "react-toastify";
import ToastWithTextOnly from "../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {roundCo2E} from "../../../utils/math/round-values";
import SearchInput from '../../../molecules/search-input/SearchInput';
import {DateTime} from 'luxon';
import {isCo2KgEValueDifferent} from "../../../utils/check/common";
import {getTotalCo2KgEForJourneyViaSumOfDays} from "../../../utils/getter/co2-getter";
import {InstanceContext} from "../../../components/protected-layout/ProtectedLayout";

interface LoadBundlesButtonProps {
    whatToLoad: BundleType,
    showBundleIcon: boolean,
    style?: CSSProperties,
    disabled?: boolean,
}

interface ResultProps {
    loadModalHeadline: string;
    attachModalHeadline: string,
    cellWeights: number[];
    iconButtonTooltip: string;
    iconStyle: Record<string, unknown>;
    icon: IconProps["icon"];
}

const getProps = (whatToLoad: BundleType, i18n: i18n, disabledNotOnProdiction: boolean) => {
    const {t} = i18n
    let result = {} as ResultProps
    switch (whatToLoad) {
        case BundleType.journey:
            result = {
                loadModalHeadline: t("GENERAL.JOURNEY"),
                attachModalHeadline: t('MODALS.READ_BUNDLES.TITLE_TRAVEL_JOURNEY'),
                cellWeights: [1.5, 1, 1, 1.25, 1, 1],
                iconButtonTooltip: disabledNotOnProdiction ? "TOOLTIP.COMMON.DISABLED_FUNCTIONALITY_DEV_INSTANCE" : "TOOLTIP.JOURNEY.LOAD_JOURNEY",
                iconStyle: {marginLeft: '3.5px'},
                icon: 'suitcase',
            }
            break
        case BundleType.travelComponents:
            result = {
                loadModalHeadline: t("GENERAL.TRAVEL_COMPONENTS"),
                attachModalHeadline: t('MODALS.READ_BUNDLES.TITLE_TRAVEL_COMPONENTS'),
                cellWeights: [1.5, 1.25, 1, 1, 1],
                iconButtonTooltip: disabledNotOnProdiction ? "TOOLTIP.COMMON.DISABLED_FUNCTIONALITY_DEV_INSTANCE" : "TOOLTIP.TRAVEL_COMPONENTS.LOAD_TRAVEL_COMPONENTS",
                iconStyle: {marginLeft: '3.5px'},
                icon: 'shapes'
            }
            break
        default:
            whatToLoad satisfies never;
    }
    return result
}

export const setInititalAgency = (agencies: AgencyProps[] | null) => agencies?.length === 1 ? agencies[0] : null

const DEFAULT_SORT_VARIABLE = SortBundle.created
const DEFAULT_SORT_ORDER = SORT_ORDER.ASC

const LoadBundlesButton = ({
                               whatToLoad,
                               showBundleIcon,
                               style,
                               disabled,
                           }: LoadBundlesButtonProps) => {
    const [attachOrReplaceLoadedTravelComponents, setAttachOrReplaceLoadedTravelComponents] = useState<"attach" | "replace" | null>(null)
    const [mockedLoadingMessageGettingData, setMockedLoadingMessageGettingData] = useState<string | null>(null)
    const [mockedLoadingMessageCalculatingEmissions, setMockedLoadingMessageCalculatingEmissions] = useState<string | null>(null);
    const {activities: travelComponentsActivities} = useAppSelector(state => state.travelComponents)
    const [searchQuery, setSearchQuery] = useState<string | null>(null)
    const {agencies} = useAppSelector(state => state.user)
    const [totalBundlesCount, setTotalBundlesCount] = useState<number | null>(null)
    const [loadBundlesModalVisible, setLoadBundlesModalVisible] = useState(false)
    const [replaceOrAttachTravelComponentsBundlesModalVisible, setReplaceOrAttachTravelComponentsBundlesModalVisible] = useState(false)
    const [replaceOrAttachJourneyBundlesModalVisible, setReplaceOrAttachJourneyBundlesModalVisible] = useState(false)
    const [rows, setRows] = useState<DataGridDataRowProps["rows"]>([]);
    const [sortVariable, setSortVariable] = useState<SortBundle>(DEFAULT_SORT_VARIABLE);
    const [sortingOrder, setSortingOrder] = useState<SortElementProps["active"]>(DEFAULT_SORT_ORDER);
    const [headers, setHeaders] = useState<DataGridHeaderRowProps["headers"]>([]);
    const [agency, setAgency] = useState<AgencyProps | null>(setInititalAgency(agencies))
    const [noContent, setNoContent] = useState(false)
    const nextScrollPosition = useRef<string | null>(null);
    const searchTerm = useRef<string | null>(null)
    const {t, i18n} = useTranslation()
    const {disableButtonOnNotProduction} = useContext(InstanceContext)
    const {resolvedLanguage} = i18n
    const [readBundles] = useLazyReadBundlesQuery()
    const [loadBundle, {
        data: bundleResponse,
        isLoading: isLoadingBundleResponse,
        isFetching: isFetchingBundleResponse,
        isError: isErrorBundleResponse,
    }] = useLazyLoadBundleQuery()
    const isDataFinished = useRef<boolean>(false)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const {
        loadModalHeadline,
        attachModalHeadline,
        iconButtonTooltip,
        cellWeights,
        iconStyle,
        icon
    } = getProps(whatToLoad, i18n, disableButtonOnNotProduction)
    const disableButton = disabled || disableButtonOnNotProduction

    useEffect(() => {
        if (isErrorBundleResponse) toast(
            <ToastWithTextOnly
                message={t("ERRORS.GENERAL_NETWORK_ERROR_BODY")}/>,
            {progress: undefined, type: "error"}
        )
    }, [isErrorBundleResponse]);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>
        if (isLoadingBundleResponse) {
            setMockedLoadingMessageGettingData(t("MODALS.READ_BUNDLES.UPDATING_PERSISTED_DATA_MOCK"));
            timer = setTimeout(() => {
                setMockedLoadingMessageGettingData(null)
                setMockedLoadingMessageCalculatingEmissions(t("MODALS.READ_BUNDLES.CALCULATING_EMISSIONS_MOCK"));
            }, 2000)
        }
        return () => {
            setMockedLoadingMessageGettingData(null)
            setMockedLoadingMessageCalculatingEmissions(null)
            clearTimeout(timer)
        }
    }, [isLoadingBundleResponse])

    useEffect(() => {
        if (bundleResponse && !isFetchingBundleResponse) {
            const {previousTotalCo2KgE, updatedTotalCo2KgE, activities, journeyDuration} = bundleResponse

            if (previousTotalCo2KgE !== null && updatedTotalCo2KgE === null) {
                toast(
                    <ToastWithTextOnly
                        message={t("TOASTS.ERROR.NO_EMISSION_ON_LOAD")}/>,
                    {progress: undefined, type: "error"}
                )
                return
            }

            if (previousTotalCo2KgE === null && updatedTotalCo2KgE === null) toast(
                <ToastWithTextOnly
                    message={t("TOASTS.ERROR.NO_EMISSION_DATA_AT_ALL_ON_LOAD")}/>,
                {progress: undefined, type: "error"}
            )

            if (previousTotalCo2KgE !== null && updatedTotalCo2KgE !== null && isCo2KgEValueDifferent(previousTotalCo2KgE, updatedTotalCo2KgE)) {
                let oldCo2KgE = roundCo2E(previousTotalCo2KgE)
                let newCo2KgE
                if (isNaN(roundCo2E(updatedTotalCo2KgE))) {
                    newCo2KgE = NO_DATA_STRING;
                } else {
                    switch (whatToLoad) {
                        case BundleType.travelComponents:
                            newCo2KgE = roundCo2E(updatedTotalCo2KgE)
                            break;
                        case BundleType.journey:
                            if (journeyDuration !== null) {
                                newCo2KgE = getTotalCo2KgEForJourneyViaSumOfDays(activities, journeyDuration, true)
                            } else {
                                newCo2KgE = roundCo2E(updatedTotalCo2KgE)
                            }
                    }
                }
                toast(
                    <ToastWithTextOnly
                        message={t("TOASTS.WARNING.DIFFERENT_CO_2_ON_LOAD_BUNDLE", {
                            oldCo2KgE,
                            newCo2KgE
                        })}/>,
                    {progress: undefined, type: "warning"}
                )
            }


            setLoadBundlesModalVisible(false)
            if (travelComponentsActivities.length > 0 && whatToLoad === BundleType.travelComponents && whatToLoad === BundleType.travelComponents) {
                setReplaceOrAttachTravelComponentsBundlesModalVisible(true)
            } else {
                switch (whatToLoad) {
                    case BundleType.journey:
                        dispatch(loadJourney(bundleResponse))
                        break;
                    case BundleType.travelComponents:
                        dispatch(loadTravelComponents(bundleResponse))
                        break;
                    default:
                        whatToLoad satisfies never
                }
                _redirect()
            }
        }
    }, [bundleResponse, isFetchingBundleResponse])

    useEffect(() => {
        if (agencies?.length === 1) setAgency(agencies[0])
    }, [agencies])

    useEffect(() => {
        const fetchData = async () => {
            if (loadBundlesModalVisible) {
                _resetOnFetchData()
                const newRows = await _fetchRows()
                setRows(newRows)
            }
        }
        fetchData().catch((error) => {
            console.error("Error fetching data:", error);
        });
    }, [agency, sortingOrder, sortVariable, resolvedLanguage, loadBundlesModalVisible])

    useEffect(() => {
        setHeaders(_setHeaders())
    }, [sortingOrder, sortVariable])

    useEffect(() => {
        if (attachOrReplaceLoadedTravelComponents !== null && bundleResponse && !isFetchingBundleResponse) {
            if (attachOrReplaceLoadedTravelComponents === "replace") {
                dispatch(replaceTravelComponentsOnLoadTravelComponents(bundleResponse))
            } else {
                dispatch(attachTravelComponentsOnLoadTravelComponents(bundleResponse))
            }
            setReplaceOrAttachTravelComponentsBundlesModalVisible(false)
            _resetForm()
        }
    }, [attachOrReplaceLoadedTravelComponents, bundleResponse, isFetchingBundleResponse])

    const _redirect = () => {
        switch (whatToLoad) {
            case BundleType.journey:
                navigate(PATH_STRINGS.journey)
                break;
            case BundleType.travelComponents:
                navigate(PATH_STRINGS.travelComponentsOverview)
                break;
            default:
                whatToLoad satisfies never
        }
    }

    const _resetForm = () => {
        setRows([])
        setTotalBundlesCount(null)
        setSearchQuery(null)
        setSortVariable(DEFAULT_SORT_VARIABLE)
        setSortingOrder(DEFAULT_SORT_ORDER)
        nextScrollPosition.current = null
        isDataFinished.current = false
        searchTerm.current = null
        setAgency(setInititalAgency(agencies))
        setNoContent(false)
        setMockedLoadingMessageGettingData(null)
        setMockedLoadingMessageCalculatingEmissions(null)
        setAttachOrReplaceLoadedTravelComponents(null)
    }

    const _fetchRows = async (): Promise<DataGridDataRowProps["rows"]> => {
        let newRows: DataGridDataRowProps["rows"] = [];
        if (agency !== null) {
            if (!isDataFinished.current) {
                try {
                    const data = await readBundles({
                        agencyId: agency?.id,
                        scrollPosition: nextScrollPosition.current,
                        size: 10,
                        sort: sortVariable,
                        sortOrder: sortingOrder,
                        direction: ScrollDirection.forward,
                        whatToGet: whatToLoad,
                        searchTerm: searchTerm.current,
                    }).unwrap();
                    nextScrollPosition.current = (data?.nextPosition ?? null);
                    if (data?.nextPosition === null || data?.nextPosition === "null") isDataFinished.current = true
                    setTotalBundlesCount(data?.bundlesCount ?? null)
                    newRows = _setRows(data);
                } catch (error) {
                    setTotalBundlesCount(null)
                    console.warn("Something went wrong reading bundles: ", error)
                }
            } else {
                return newRows
            }
        } else {
            setTotalBundlesCount(null)
            return newRows
        }
        return newRows;
    };

    const _setRows = (data: { bundles: TransformedBundleResponse[] }) => {
        const bundles = data?.bundles ?? [];
        return bundles.flatMap(bundle => {
            const {
                name,
                startDate,
                endDate,
                updated,
                userFullName,
                totalCo2KgE,
                durationInDays,
                created
            } = bundle
            const updatedDateMillis = updated ? updated.toMillis() : null
            const createdDateMillis = created ? created.toMillis() : null

            const commonRows = {
                data: [
                    {
                        text: name || NO_DATA_STRING,
                        value: name || '',
                        keyPathToValue: "bundle.name",
                    },
                    {
                        text: userFullName || NO_DATA_STRING,
                        value: userFullName || NO_DATA_STRING,
                        keyPathToValue: "bundle.userFullName",
                    },
                    {
                        text: createdDateMillis ? formatDateString(createdDateMillis, resolvedLanguage, true) : NO_DATA_STRING,
                        value: createdDateMillis ?? -1,
                        keyPathToValue: "bundle.createdDate",
                    },
                    {
                        text: updatedDateMillis ? formatDateString(updatedDateMillis, resolvedLanguage, true) : NO_DATA_STRING,
                        value: updatedDateMillis ?? -1,
                        keyPathToValue: "bundle.updatedDate",
                    },
                    {
                        text: `${totalCo2KgE ?? 0} kg CO2e`,
                        value: totalCo2KgE ?? 0,
                        keyPathToValue: "bundle.co2KgETotal",
                    },
                ],
                key: uuidv4(),
                rawData: bundle,
            }
            switch (whatToLoad) {
                case BundleType.travelComponents:
                    return commonRows
                case BundleType.journey:
                    break
                default:
                    whatToLoad satisfies never
            }
            let journeyDurationInDays
            const startDateMillis = startDate ? startDate.toMillis() : null
            const endDateMillis = endDate ? endDate.toMillis() : null
            if (startDateMillis !== null && endDateMillis !== null) {
                journeyDurationInDays = getDaysCountBetweenDates(DateTime.fromMillis(startDateMillis), DateTime.fromMillis(endDateMillis))
            } else {
                journeyDurationInDays = durationInDays
            }
            commonRows.data.push({
                text: journeyDurationInDays !== null ? journeyDurationInDays.toString() : NO_DATA_STRING,
                value: journeyDurationInDays ?? -1,
                keyPathToValue: "bundle.duration",
            })
            return commonRows
        })
    }

    const _setHeaders = () => {
        const commonHeaders = [
            {
                text: "GENERAL.NAME",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortBundle.name || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortBundle.name);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetchData()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortBundle.name || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortBundle.name);
                            setSortingOrder(SORT_ORDER.DESC)
                            _resetOnFetchData()
                        }
                    },
                    disabled: false,
                },
            },
            {
                text: "GENERAL.USER",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortBundle.userFirstName || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortBundle.userFirstName);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetchData()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortBundle.userFirstName || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortBundle.userFirstName);
                            setSortingOrder(SORT_ORDER.DESC);
                            _resetOnFetchData()
                        }
                    },
                    disabled: false,
                },
            },
            {
                text: "GENERAL.CREATED",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortBundle.created || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortBundle.created);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetchData()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortBundle.created || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortBundle.created);
                            setSortingOrder(SORT_ORDER.DESC)
                            _resetOnFetchData()
                        }
                    },
                    disabled: false,
                },
            },
            {
                text: "GENERAL.EDITED",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortBundle.updated || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortBundle.updated);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetchData()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortBundle.updated || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortBundle.updated);
                            setSortingOrder(SORT_ORDER.DESC)
                            _resetOnFetchData()
                        }
                    },
                    disabled: false,
                },
            },
            {
                text: "GENERAL.CO2KGE",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortBundle.totalCo2KgE || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortBundle.totalCo2KgE);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetchData()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortBundle.totalCo2KgE || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortBundle.totalCo2KgE);
                            setSortingOrder(SORT_ORDER.DESC)
                            _resetOnFetchData()
                        }
                    },
                    disabled: false,
                },
            }
        ]
        switch (whatToLoad) {
            case BundleType.travelComponents:
                return commonHeaders
            case BundleType.journey:
                break
            default:
                whatToLoad satisfies never;
        }

        return [...commonHeaders, {text: `${t("GENERAL.DURATION")} (${t("GENERAL.DAYS")})`,}]
    }

    const _resetOnFetchData = () => {
        nextScrollPosition.current = null
        isDataFinished.current = false
        setTotalBundlesCount(null)
        setRows([]);
    }

    const _onChange = async (query: string) => {
        nextScrollPosition.current = null
        isDataFinished.current = false
        const queryForSearch = query.length > 0 ? query : null
        searchTerm.current = queryForSearch
        setSearchQuery(queryForSearch)
        const newRows = await _fetchRows()
        setRows(newRows)
    }

    const _renderAgencySelectionDropdown = (disabled: boolean) => <DropdownInput
        readOnly={disabled}
        aria-errormessage={"ERRORS.COMMON.NAME"}
        required={false}
        containerStyle={{width: '100%', marginBottom: theme.spacer.large}}
        defaultValue={agency?.id}
        label={"GENERAL.AGENCY"}
        options={convertAgenciesToDropdownFormat(agencies!!)}
        setSelection={selection => {
            const selectedAgency = convertAgenciesFromDropdownFormat(selection)
            setAgency(selectedAgency)
            nextScrollPosition.current = null
        }}
    />

    const _renderContent = () => {
        let ContentToRender: ReactNode
        if (agencies === null) {
            ContentToRender = <Text classes={"load-bundles-button-select-input-message"}>
                {t('MODALS.READ_BUNDLES.NO_AGENCY', {whatToLoad: loadModalHeadline})}
            </Text>
        } else if (agency !== null) {
            ContentToRender = <>
                {
                    agencies.length > 1 && <div
                        className={"load-bundles-dropdown-with-datagrid-rows"}>
                        {_renderAgencySelectionDropdown(false)}
                    </div>
                }
                {rows.length > 0 &&
                    <div style={{width: '100%', marginBottom: theme.spacer.large}}>
                        <SearchInput
                            value={searchQuery}
                            label={"GENERAL.SEARCH"}
                            placeholder={whatToLoad === BundleType.journey ? "GENERAL.JOURNEY" : "GENERAL.TRAVEL_COMPONENTS"}
                            onChange={_onChange}
                        />
                    </div>}
                <DataGrid
                    noDataText={"MODALS.READ_BUNDLES.NO_RECORDS"}
                    look={"primaryLight"}
                    cellWeights={cellWeights}
                    headers={headers}
                    rows={rows}
                    rowSize={"medium"}
                    hover={true}
                    onClickRow={(_, bundle) => loadBundle(bundle.id)}
                    fetchRows={_fetchRows}
                    textAdornment={totalBundlesCount !== null ? `${t("GENERAL.TOTAL")}: ${totalBundlesCount} ${t("GENERAL.DATASETS")}` : undefined}
                />
            </>
        } else if (agencies.length > 1) {
            ContentToRender = <div className={'load-bundles-spinner-wrapper'}>
                <Text classes={"load-bundles-button-message"}>
                    MODALS.READ_BUNDLES.AGENCY_SELECTION_MESSAGE
                </Text>
                {agencies.length > 1 && _renderAgencySelectionDropdown(false)}
            </div>
        }

        return ContentToRender
    }

    const _onCloseModal = () => {
        setLoadBundlesModalVisible(false)
        _resetForm()
    }

    return <>
        <div
            className={clsx(
                "load-bundles-button-container",
                !disableButton && "button-icon-only-hover",
            )}
            style={{position: "relative", ...style}}>
            <IconButton
                disabled={disableButton}
                tooltip={iconButtonTooltip}
                color={"primary"}
                size={"large"}
                onlyIcon={true}
                icon={"folderOpen"}
                onClick={() => {
                    setLoadBundlesModalVisible(true)
                }}
            />
            {showBundleIcon &&
                <Icon
                    color={theme.colors.primary}
                    disabled={disableButton}
                    iconStyle={iconStyle}
                    icon={icon}
                    size={"small"}
                    containerClasses={clsx(!disableButton && "load-bundles-button-travel-component-icon-wrapper", "load-bundles-button-travel-component-icon-position")}/>
            }
        </div>
        <Modal
            paperClasses={clsx(noContent && 'load-travel-components-modal-paper-no-content')}
            closeButtonText={"GENERAL.ABORT"}
            onBackdropClick={() => _onCloseModal()}
            onClose={() => _onCloseModal()}
            headline={attachModalHeadline}
            showPaper={true}
            show={loadBundlesModalVisible}>
            <div className='load-bundles-container'>
                {
                    isLoadingBundleResponse ?
                        <div className={"load-bundles-button-loading-bundles-wrapper"}>
                            <Spinner size={SMALL_SPINNER_SIZE} fullPage={false}/>
                            {mockedLoadingMessageGettingData && <Text>{mockedLoadingMessageGettingData}</Text>}
                            {mockedLoadingMessageCalculatingEmissions &&
                                <Text>{mockedLoadingMessageCalculatingEmissions}</Text>}
                        </div> :
                        _renderContent()
                }
            </div>
        </Modal>
        <Modal
            closeButtonText={capitalizeFirstLetter(t("GENERAL.ATTACH"))}
            confirmButtonColor={"warningMain"}
            confirmButtonText={capitalizeFirstLetter(t("GENERAL.REPLACE"))}
            confirmButtonStyles={{borderColor: theme.colors.warningMain}}
            confirmButtonTextStyles={{color: theme.colors.white}}
            onBackdropClick={() => setReplaceOrAttachTravelComponentsBundlesModalVisible(false)}
            onClose={() => {
                setAttachOrReplaceLoadedTravelComponents("attach")
                _redirect()
            }}
            onConfirm={() => {
                setAttachOrReplaceLoadedTravelComponents("replace")
                _redirect()
            }}
            headline={"MODALS.REPLACE_OR_ATTACH_TRAVEL_COMPONENTS.TITLE"}
            showPaper={true}
            show={replaceOrAttachTravelComponentsBundlesModalVisible}>
            <Text>MODALS.REPLACE_OR_ATTACH_TRAVEL_COMPONENTS.TEXT</Text>
        </Modal>
        <Modal
            closeButtonText={capitalizeFirstLetter(t("GENERAL.ABORT"))}
            confirmButtonColor={"warningMain"}
            confirmButtonText={capitalizeFirstLetter(t("GENERAL.REPLACE"))}
            confirmButtonStyles={{borderColor: theme.colors.warningMain}}
            confirmButtonTextStyles={{color: theme.colors.white}}
            onBackdropClick={() => setReplaceOrAttachJourneyBundlesModalVisible(false)}
            onClose={() => {
                setReplaceOrAttachJourneyBundlesModalVisible(false)
            }}
            onConfirm={() => {
                bundleResponse && dispatch(loadJourney(bundleResponse))
                setReplaceOrAttachJourneyBundlesModalVisible(false)
                _redirect()
            }}
            headline={"MODALS.REPLACE_JOURNEY.TITLE"}
            showPaper={true}
            show={replaceOrAttachJourneyBundlesModalVisible}>
            <Text>MODALS.REPLACE_JOURNEY.TEXT</Text>
        </Modal>
    </>
}

export default LoadBundlesButton